import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const GRI407 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    atRiskOperationsAndSuppliers: {
      suppliers: "",
      operationAndSupplierType: "",
      atRiskCountriesOrAreas: "",
    },

    collectiveBargaining: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);

        const data = response.data.data.GRI400?.freedomOfAssociation || {};
        setFormData({
          atRiskOperationsAndSuppliers: {
            suppliers: data.atRiskOperationsAndSuppliers?.suppliers || "",
            atRiskCountriesOrAreas:
              data.atRiskOperationsAndSuppliers?.atRiskCountriesOrAreas || "",
            operationAndSupplierType:
              data.atRiskOperationsAndSuppliers?.operationAndSupplierType || "",
          },
          collectiveBargaining: data.collectiveBargaining || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.atRiskOperationsAndSuppliers.suppliers) {
      newErrors.suppliers = "This field is required";
      formIsValid = false;
    }

    if (!formData.atRiskOperationsAndSuppliers.operationAndSupplierType) {
      newErrors.operationAndSupplierType = "This field is required";
      formIsValid = false;
    }

    if (!formData.atRiskOperationsAndSuppliers.atRiskCountriesOrAreas) {
      newErrors.atRiskCountriesOrAreas = "This field is required";
      formIsValid = false;
    }

    if (!formData.collectiveBargaining) {
      newErrors.collectiveBargaining = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;

      const payload = {
        type: "GRI400",
        submitted407: true,
        freedomOfAssociation: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/408-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/408-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      <p
        className={styles.section_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        407 : Freedom of Association
      </p>
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        407-1 : Operations and suppliers in which the right to freedom of
        association and collective bargaining may be at risk
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <SectionHeading text="a. Can you identify operations and suppliers where there is a risk of workers' rights to exercise freedom of association or collective bargaining being violated or significantly at risk? Please specify:" />
        <CustomTextField
          value={formData.atRiskOperationsAndSuppliers.suppliers}
          onChange={(e) =>
            handleNestedInputChange(
              "atRiskOperationsAndSuppliers",
              "suppliers",
              e.target.value
            )
          }
          error={errors.suppliers}
          helperText={errors.suppliers}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. The type of operation and supplier." />
          <CustomTextField
            value={
              formData.atRiskOperationsAndSuppliers.operationAndSupplierType
            }
            onChange={(e) =>
              handleNestedInputChange(
                "atRiskOperationsAndSuppliers",
                "operationAndSupplierType",
                e.target.value
              )
            }
            error={errors.operationAndSupplierType}
            helperText={errors.operationAndSupplierType}
            disabled={disabled}
          />

          <SectionHeading text="ii. Countries or geographic areas where operations and suppliers are considered at risk." />
          <CustomTextField
            value={formData.atRiskOperationsAndSuppliers.atRiskCountriesOrAreas}
            onChange={(e) =>
              handleNestedInputChange(
                "atRiskOperationsAndSuppliers",
                "atRiskCountriesOrAreas",
                e.target.value
              )
            }
            error={errors.atRiskCountriesOrAreas}
            helperText={errors.atRiskCountriesOrAreas}
            disabled={disabled}
          />
        </Box>

        <SectionHeading
          text="b. What actions did the organization take during the reporting period to support the rights of workers to exercise freedom of association and collective bargaining?"
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.collectiveBargaining}
          onChange={(e) =>
            handleInputChange("collectiveBargaining", e.target.value)
          }
          error={errors.collectiveBargaining}
          helperText={errors.collectiveBargaining}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI407;
